import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { aboutFeatures, sustainFeatures } from "@/mocks/home";
import { useHomeCategories } from "../hooks/useHomeCategories";

const btnOutline =
  "inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors";
const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const h2 =
  "font-heading text-3xl md:text-5xl !leading-[1.3] text-foreground-950 mb-5";

type Labeled = { title: string; desc?: string };
type Stat = { value: string; label: string };

export function Hero() {
  const { t } = useTranslation();
  return (
    <section
      id="home"
      className="relative h-[620px] md:h-[80vh] lg:h-screen w-full overflow-hidden"
    >
      <img
        src="https://readdy.ai/api/search-image?query=Vast%20expansive%20bright%20blue%20sky%20with%20soft%20white%20clouds%20filling%20most%20of%20the%20frame%20above%20a%20serene%20Vietnamese%20rice%20field%20landscape%2C%20winding%20river%20reflecting%20the%20blue%20sky%2C%20distant%20gentle%20mountains%2C%20lush%20green%20paddies%20in%20lower%20third%2C%20fresh%20vibrant%20airy%20tones%2C%20spacious%20bright%20left%20side%20for%20text%20overlay&width=1920&height=1080&seq=lam-hero-01&orientation=landscape&nocache=true"
        alt="Vietnamese rice fields"
        title="Vietnamese rice fields EverRice"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/80 via-background-50/40 to-transparent" />
      <div className="relative w-full h-full flex items-center page-gutter">
        <div className="max-w-xl fade-up pt-20">
          <p className="font-heading text-xl font-medium tracking-[0.3em] text-foreground-700 mb-4">
            {t("home.hero.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl md:text-6xl !leading-[1.4] tracking-[0.03em] text-foreground-950 mb-6">
            {t("home.hero.title")}
          </h1>
          <p className="text-sm md:text-base text-foreground-800 mb-8 leading-relaxed">
            {t("home.hero.sub1")}
            <br />
            {t("home.hero.sub2")}
          </p>
          <a
            href="#categories"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer"
          >
            {t("home.hero.cta")} <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}

export function About() {
  const { t } = useTranslation();
  const features = t("home.about.features", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section
      id="about-us"
      className="relative bg-background-50 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 page-gutter py-16 md:py-24 items-center">
        <div>
          <p className={eyebrow}>{t("home.about.eyebrow")}</p>
          <h2 className={h2}>
            {t("home.about.title1")}
            <br />
            {t("home.about.title2")}
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed mb-8 max-w-md">
            {t("home.about.desc")}
          </p>
          <Link to="/about-us" className={btnOutline}>
            {t("home.about.cta")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div key={f.title} className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full border border-background-300 bg-background-100 flex items-center justify-center mb-4">
                <i
                  className={`${aboutFeatures[i].icon} text-2xl text-primary-600`}
                />
              </div>
              <h3 className="text-xs tracking-widest uppercase font-medium mb-2">
                {f.title}
              </h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// SECTION HIỂN THỊ DANH MỤC SẢN PHẨM (MAPPED TỪ API THEO MULTI-LANG)
export function Products() {
  const { t } = useTranslation();
  const { categories, isLoading, hasData } = useHomeCategories();

  return (
    <section
      id="categories"
      className="bg-primary-50 page-gutter py-16 md:py-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-center">
        <div>
          <p className={eyebrow}>
            {t("home.categories.eyebrow", t("home.products.eyebrow"))}
          </p>
          <h2 className={`${h2} whitespace-pre-line`}>
            {t("home.categories.title", t("home.products.title"))}
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed mb-8">
            {t("home.categories.desc", t("home.products.desc"))}
          </p>
          <Link to="/products" className={btnOutline}>
            {t("home.categories.cta", t("home.products.cta"))}{" "}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-xs text-foreground-500">
            Đang tải danh mục...
          </div>
        ) : !hasData ? (
          <div className="py-12 text-center text-xs text-foreground-500 bg-background-50 rounded-lg border border-background-200">
            Chưa có danh mục nào cho ngôn ngữ này.
          </div>
        ) : (
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            data-category-shop
          >
            {categories.map((c: any) => (
              <Link
                to="/products"
                key={c.id || c.name}
                className="bg-background-50 rounded-lg overflow-hidden border border-background-200 group cursor-pointer block hover:shadow-md transition-shadow"
              >
                <div className="w-full h-40 overflow-hidden bg-background-200">
                  <img
                    src={c.img}
                    alt={c.name}
                    title={`${c.name} EverRice`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="text-xs tracking-widest uppercase font-medium line-clamp-1">
                    {c.name}
                  </h3>
                  <p className="text-xs text-foreground-600 mt-1 line-clamp-2">
                    {c.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function Quality() {
  const { t } = useTranslation();
  const stats = t("home.stats", { returnObjects: true }) as unknown as Stat[];
  return (
    <section id="quality" className="bg-background-50 page-gutter py-14">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`rounded-lg p-6 text-center transition-transform duration-300 hover:-translate-y-1 ${i === 1 ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg" : "bg-background-50 border border-background-200 shadow-md"}`}
          >
            <p className="font-heading text-4xl md:text-5xl">{s.value}</p>
            <p
              className={`text-xs tracking-widest uppercase mt-2 ${i === 1 ? "text-background-100" : "text-foreground-800"}`}
            >
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function GlobalReach() {
  const { t } = useTranslation();
  return (
    <section
      id="global-reach"
      className="relative h-[460px] md:h-[520px] overflow-hidden"
    >
      <img
        src="https://public.readdy.ai/ai/img_res/edited_954a9e5d3637775424f383df1645abfe_9fe2afaf.jpg"
        alt="Global rice export shipping"
        title="EverRice global rice export"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background-50/50 to-transparent" />
      <div className="relative h-full flex items-center page-gutter lg:pl-[22%]">
        <div className="max-w-[43.75rem]">
          <p className={eyebrow}>{t("home.globalReach.eyebrow")}</p>
          <h2 className={h2}>{t("home.globalReach.title")}</h2>
          <p className="text-sm text-foreground-800 leading-relaxed mb-8">
            {t("home.globalReach.desc")}
          </p>
          <Link to="/global-reach" className={btnOutline}>
            {t("home.globalReach.cta")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function Sustainability() {
  const { t } = useTranslation();
  const features = t("home.sustainability.features", {
    returnObjects: true,
  }) as unknown as { title: string }[];
  return (
    <section
      id="sustainability"
      className="grid grid-cols-1 lg:grid-cols-2 bg-background-50"
    >
      <div className="w-full h-72 lg:h-auto">
        <img
          src="https://readdy.ai/api/search-image?query=Macro%20close%20up%20of%20fresh%20green%20rice%20leaf%20with%20clear%20water%20dew%20drops%2C%20soft%20bokeh%20bright%20green%20background%2C%20morning%20light%2C%20fresh%20clean%20nature%20photography&width=960&height=560&seq=lam-sustain-01&orientation=landscape"
          alt="Green rice leaf with dew"
          title="Sustainable rice farming"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="page-gutter py-14 md:py-20 flex flex-col gap-9 justify-center">
        <div>
          <p className={eyebrow}>{t("home.sustainability.eyebrow")}</p>
          <h2 className="font-heading text-[2rem] font-light leading-[1.3] mb-4 whitespace-pre-line">
            {t("home.sustainability.title")}
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed">
            {t("home.sustainability.desc")}
          </p>
        </div>
        <div className="flex flex-wrap gap-6">
          {features.map((f, i) => (
            <div key={f.title} className="text-center w-24">
              <div className="w-14 h-14 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                <i
                  className={`${sustainFeatures[i].icon} text-xl text-primary-600`}
                />
              </div>
              <p className="text-[10px] tracking-widest uppercase font-medium leading-relaxed">
                {f.title}
              </p>
            </div>
          ))}
        </div>
        <div>
          <Link to="/sustainability" className={btnOutline}>
            {t("home.sustainability.cta")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
